import React from 'react';

import isArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import SidepanelItem, {SidepanelItemProps} from 'nav/sidepanel/listing/SidepanelItem';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {apiIdToIconName} from 'util/APIUtil';

import {AnalysisSearchAttributesGraphQL} from '../../../../graphql-fragments/analysis-tool/__generated/AnalysisSearchAttributesGraphQL';
import {AnalyticsDashboardSearchAttributesGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsDashboardSearchAttributesGraphQL';

type SearchAttributesGraphQL = AnalysisSearchAttributesGraphQL | AnalyticsDashboardSearchAttributesGraphQL;

export type BaseAnalyticsSidepanelItemProps<T extends SearchAttributesGraphQL> = {
  item: T;
  openResult: (item: T) => void;
  trackOpenEvent?: (item: T) => void;
  isChild: boolean;
} & Pick<SidepanelItemProps, 'active' | 'handleClickStar' | 'isCompact' | 'starred'>;

export default function BaseAnalyticsSidepanelItem<T extends SearchAttributesGraphQL>(
  props: BaseAnalyticsSidepanelItemProps<T>
): JSX.Element {
  const handleOpen = (): void => {
    props.openResult(props.item);
    props.trackOpenEvent?.(props.item);
  };

  const {prefix, date} = FileBrowserUtil.getResultDate(props.item, {
    fuzzy: true,
    compact: props.isCompact,
  });

  // When compact, a SidepanelItem shows only either a subtitle or a star icon.
  // To only unstarrable items showing dates, never show a subtitle if compact.
  const dateString = `${prefix}${date}`;
  const subtitle = props.isCompact ? undefined : dateString;

  const featureStatus = getFeatureStatus({feature: StandardFeature.OPEN, item: props.item});

  return (
    <SidepanelItem
      active={props.active}
      className="sidepanelItem--noIcon"
      faded={!featureStatus.enabled || (isArchivable(props.item) && isArchived(props.item))}
      handleClickStar={props.handleClickStar}
      icon={<BenchlingIcon icon={apiIdToIconName(props.item.id)} />}
      isCompact={props.isCompact}
      onClick={handleOpen}
      parties={[{...props.item.creator, avatar_url: props.item.creator.avatarUrl!}]}
      showStar={props.handleClickStar != null}
      subTitle={subtitle}
      starred={props.starred}
      title={{
        text: props.item.name,
        node: <SidepanelItemTitle name={props.item.name} />,
      }}
      tooltipText={featureStatus.enabled ? '' : featureStatus.userMessage}
      isExpandable={false}
      isChild={props.isChild}
    />
  );
}

function SidepanelItemTitle(props: {name: string}): JSX.Element {
  return <span className="sidepanel-primaryLabel">{props.name}</span>;
}
